import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import {
  LogOut, Users, Sparkles, CreditCard, HelpCircle,
  GraduationCap, Menu, X, UserCircle2, Palette, Home, ChevronDown,
} from "lucide-react";

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const userMenuRef = useRef(null);

  // Close mobile menu whenever route changes
  useEffect(() => { setMobileOpen(false); setUserMenuOpen(false); }, [loc.pathname]);

  // Close user dropdown on outside click
  useEffect(() => {
    if (!userMenuOpen) return;
    const onClickOutside = (e) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target)) {
        setUserMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, [userMenuOpen]);

  // Lock body scroll while the mobile menu is open
  useEffect(() => {
    if (mobileOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => { document.body.style.overflow = ""; };
  }, [mobileOpen]);

  const handleLogout = () => {
    logout();
    nav("/login");
  };

  const NAV_LINKS = [
    { to: "/",         label: "Home",       icon: Home,          testid: "nav-home" },
    { to: "/clienti",  label: "Clienti",    icon: Users,         testid: "nav-clients" },
    { to: "/piani",    label: "Piani",      icon: CreditCard,    testid: "nav-plans" },
    { to: "/academy",  label: "Academy",    icon: GraduationCap, testid: "nav-academy" },
    { to: "/beta-color", label: "Beta Color", icon: Palette,      testid: "nav-beta-color", badge: "BETA" },
    { to: "/profilo",  label: "Profilo",    icon: UserCircle2,   testid: "nav-profile" },
    { to: "/aiuto",    label: "Supporto",   icon: HelpCircle,    testid: "nav-help" },
  ];

  // Desktop top-bar nav — items moved to the user dropdown are excluded here
  const MAIN_NAV_LINKS = NAV_LINKS.filter(
    (n) => !["/piani", "/profilo", "/aiuto", "/beta-color"].includes(n.to)
  );

  // User dropdown items, in the requested order
  const USER_MENU_LINKS = [
    { to: "/profilo",    label: "Profilo",    icon: UserCircle2, testid: "nav-profile" },
    { to: "/piani",      label: "Piani",      icon: CreditCard,  testid: "nav-plans" },
    { to: "/aiuto",      label: "Supporto",   icon: HelpCircle,  testid: "nav-help" },
    { to: "/beta-color", label: "Beta Color", icon: Palette,     testid: "nav-beta-color", badge: "BETA" },
  ];

  const linkClass = (active) =>
    `text-sm font-medium transition-colors ${
      active ? "text-[#C5A059]" : "text-[#737373] hover:text-[#1A1A1A]"
    }`;

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      <header className="sticky top-0 z-30 bg-white/80 backdrop-blur-md border-b border-[#E5E5E0]">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3 group" data-testid="logo-link">
            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-[#C5A059] to-[#B38F4E] flex items-center justify-center">
              <Sparkles className="w-4 h-4 text-white" strokeWidth={2} />
            </div>
            <div className="flex flex-col leading-none">
              <span className="font-serif-display text-xl text-[#1A1A1A]">AI TricoProtocol</span>
              <span className="text-[10px] tracking-[0.2em] text-[#C5A059] uppercase mt-0.5">Professional Edition</span>
            </div>
          </Link>

          {/* Desktop nav */}
          <nav className="hidden md:flex items-center gap-8">
            {MAIN_NAV_LINKS.map((n) => {
              const Icon = n.icon;
              return (
                <Link
                  key={n.to}
                  to={n.to}
                  data-testid={n.testid}
                  className={`${linkClass(loc.pathname === n.to)} inline-flex items-center gap-1.5`}
                >
                  <Icon className="w-4 h-4" />
                  {n.label}
                  {n.badge && (
                    <span className="ml-1 px-1.5 py-0.5 rounded-full bg-[#FAF6EE] border border-[#C5A059]/40 text-[9px] font-semibold text-[#8A6B2E] tracking-wider">
                      {n.badge}
                    </span>
                  )}
                </Link>
              );
            })}
          </nav>

          <div className="flex items-center gap-3">
            {/* User dropdown trigger */}
            <div className="relative hidden sm:block" ref={userMenuRef}>
              <button
                onClick={() => setUserMenuOpen((o) => !o)}
                className="flex items-center gap-2 rounded-full pl-1 pr-2 py-1 hover:bg-[#EAE5DF] transition-colors"
                data-testid="user-menu-toggle"
                aria-haspopup="true"
                aria-expanded={userMenuOpen}
              >
                <div className="w-8 h-8 rounded-full bg-[#EAE5DF] flex items-center justify-center">
                  <UserCircle2 className="w-5 h-5 text-[#737373]" />
                </div>
                <div className="flex flex-col items-start leading-none">
                  <span className="text-sm font-medium text-[#1A1A1A]" data-testid="user-name">{user?.name}</span>
                  <span className="text-xs text-[#737373] mt-0.5">{user?.email}</span>
                </div>
                <ChevronDown className={`w-4 h-4 text-[#737373] transition-transform ${userMenuOpen ? "rotate-180" : ""}`} />
              </button>

              {/* Dropdown panel */}
              <div
                className={`absolute right-0 mt-2 w-56 rounded-xl border border-[#E5E5E0] bg-white shadow-xl py-2 transition-all origin-top-right ${
                  userMenuOpen ? "opacity-100 scale-100 pointer-events-auto" : "opacity-0 scale-95 pointer-events-none"
                }`}
                data-testid="user-menu-dropdown"
              >
                {USER_MENU_LINKS.map((n) => {
                  const Icon = n.icon;
                  const active = loc.pathname === n.to;
                  return (
                    <Link
                      key={n.to}
                      to={n.to}
                      data-testid={n.testid}
                      onClick={() => setUserMenuOpen(false)}
                      className={`flex items-center gap-3 px-4 py-2.5 text-sm font-medium transition-colors ${
                        active ? "text-[#C5A059] bg-[#FAF6EE]" : "text-[#1A1A1A] hover:bg-[#FAFAFA]"
                      }`}
                    >
                      <Icon className={`w-4 h-4 ${active ? "text-[#C5A059]" : "text-[#737373]"}`} />
                      <span className="inline-flex items-center gap-2">
                        {n.label}
                        {n.badge && (
                          <span className="px-1.5 py-0.5 rounded-full bg-[#FAF6EE] border border-[#C5A059]/40 text-[9px] font-semibold text-[#8A6B2E] tracking-wider">
                            {n.badge}
                          </span>
                        )}
                      </span>
                    </Link>
                  );
                })}
                <div className="my-2 border-t border-[#E5E5E0]" />
                <button
                  onClick={handleLogout}
                  className="w-full flex items-center gap-3 px-4 py-2.5 text-sm font-medium text-[#1A1A1A] hover:bg-[#FAFAFA] transition-colors"
                  data-testid="logout-button"
                >
                  <LogOut className="w-4 h-4 text-[#737373]" />
                  Logout
                </button>
              </div>
            </div>

            {/* Hamburger — visible only on mobile */}
            <button
              onClick={() => setMobileOpen(true)}
              className="md:hidden w-10 h-10 rounded-full border border-[#E5E5E0] hover:bg-[#EAE5DF] transition-colors flex items-center justify-center"
              data-testid="mobile-menu-toggle"
              aria-label="Apri menu"
              aria-expanded={mobileOpen}
            >
              <Menu className="w-4 h-4 text-[#1A1A1A]" />
            </button>
          </div>
        </div>
      </header>

      {/* Mobile drawer */}
      <div
        className={`md:hidden fixed inset-0 z-40 transition-opacity duration-300 ${
          mobileOpen ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
        }`}
        data-testid="mobile-menu-overlay"
      >
        {/* Backdrop */}
        <div
          className="absolute inset-0 bg-[#1A1A1A]/40 backdrop-blur-sm"
          onClick={() => setMobileOpen(false)}
        />

        {/* Sliding panel */}
        <aside
          className={`absolute top-0 right-0 h-full w-[82%] max-w-[340px] bg-white shadow-2xl flex flex-col transition-transform duration-300 ease-out ${
            mobileOpen ? "translate-x-0" : "translate-x-full"
          }`}
          data-testid="mobile-menu-panel"
        >
          {/* Drawer header */}
          <div className="flex items-center justify-between px-6 h-16 border-b border-[#E5E5E0]">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-[#C5A059] to-[#B38F4E] flex items-center justify-center">
                <Sparkles className="w-3.5 h-3.5 text-white" strokeWidth={2} />
              </div>
              <span className="font-serif-display text-lg text-[#1A1A1A]">Menu</span>
            </div>
            <button
              onClick={() => setMobileOpen(false)}
              className="w-9 h-9 rounded-full hover:bg-[#F2EEE7] flex items-center justify-center transition-colors"
              data-testid="mobile-menu-close"
              aria-label="Chiudi menu"
            >
              <X className="w-4 h-4 text-[#1A1A1A]" />
            </button>
          </div>

          {/* User block */}
          <div className="px-6 py-5 border-b border-[#E5E5E0]">
            <p className="text-[10px] tracking-[0.2em] uppercase text-[#C5A059] font-medium mb-1">
              Account
            </p>
            <p className="text-sm font-medium text-[#1A1A1A] truncate">{user?.name}</p>
            <p className="text-xs text-[#737373] truncate">{user?.email}</p>
          </div>

          {/* Nav items */}
          <nav className="flex-1 overflow-y-auto py-3">
            {NAV_LINKS.map((n) => {
              const Icon = n.icon;
              const active = loc.pathname === n.to;
              return (
                <Link
                  key={n.to}
                  to={n.to}
                  data-testid={`mobile-${n.testid}`}
                  className={`flex items-center gap-4 px-6 py-4 text-base font-medium transition-all border-l-2 ${
                    active
                      ? "text-[#C5A059] bg-[#FAF6EE] border-[#C5A059]"
                      : "text-[#1A1A1A] hover:bg-[#FAFAFA] border-transparent"
                  }`}
                >
                  <Icon className={`w-5 h-5 ${active ? "text-[#C5A059]" : "text-[#737373]"}`} />
                  <span className="inline-flex items-center gap-2">
                    {n.label}
                    {n.badge && (
                      <span className="px-1.5 py-0.5 rounded-full bg-[#FAF6EE] border border-[#C5A059]/40 text-[9px] font-semibold text-[#8A6B2E] tracking-wider">
                        {n.badge}
                      </span>
                    )}
                  </span>
                </Link>
              );
            })}
          </nav>

          {/* Drawer footer / logout */}
          <div className="p-6 border-t border-[#E5E5E0] space-y-4">
            <button
              onClick={handleLogout}
              className="w-full h-11 rounded-xl border border-[#E5E5E0] hover:border-[#C5A059] text-[#1A1A1A] font-medium inline-flex items-center justify-center gap-2 transition-all"
              data-testid="mobile-logout-button"
            >
              <LogOut className="w-4 h-4" />
              Esci
            </button>
          </div>
        </aside>
      </div>

      <main className="max-w-7xl mx-auto px-6 lg:px-10 py-10">
        {children}
      </main>

      {/* Footer */}
      <footer className="mt-16 border-t border-[#E5E5E0] bg-white/60" data-testid="site-footer">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-8 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2 text-xs text-[#737373]">
            <Sparkles className="w-3.5 h-3.5 text-[#C5A059]" />
            <span className="font-medium text-[#1A1A1A]">AI TricoProtocol</span>
            <span className="hidden sm:inline">· Professional Edition</span>
          </div>

          <nav className="flex items-center gap-6 text-sm">
            <Link
              to="/updates"
              data-testid="footer-updates-link"
              className="text-[#737373] hover:text-[#C5A059] transition-colors"
            >
              Updates
            </Link>
            <Link
              to="/academy"
              data-testid="footer-academy-link"
              className="text-[#737373] hover:text-[#C5A059] transition-colors"
            >
              Academy
            </Link>
            <Link
              to="/aiuto"
              data-testid="footer-help-link"
              className="text-[#737373] hover:text-[#C5A059] transition-colors"
            >
              Guida
            </Link>
            <a
              href="mailto:supporto@aitricoprotocol.com"
              data-testid="footer-support-link"
              className="text-[#737373] hover:text-[#C5A059] transition-colors"
            >
              Supporto
            </a>
          </nav>
        </div>
      </footer>
    </div>
  );
}
